import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Buildings, CheckSquare, DotsSixVertical, FloppyDisk, NotePencil, Plus,
  Square, Trash, X,
} from '@phosphor-icons/react';
import { api } from '@/api/client';
import {
  useFases, useEstadosLote,
  useCreateFase, useUpdateFase, useDeleteFase, useReorderFases,
  useCreateEstadoLote, useUpdateEstadoLote, useDeleteEstadoLote, useReorderEstadosLote,
} from '@/hooks/useFases';
import type { EstadoLote, Fase } from '@/types';

// Tipo de proyecto sugerido en el dialog. La empresa solo opera con
// "Permiso Vivienda" (los estados de "Otros" son APC EN). Lo dejamos
// editable por si el negocio agrega categorias en el futuro.
const TIPOS_PROYECTO = ['Permiso Vivienda', 'Otros'];

// ============================================================
// FasesPanel: master-detail. Izquierda lista de fases + DnD reorder +
// edit inline (nombre/color); derecha la lista de estados de la fase
// seleccionada con su propio reorder + dialog de edit completo.
// ============================================================
export function FasesPanel() {
  const { data: fases = [], isLoading } = useFases();
  const [selectedFaseId, setSelectedFaseId] = useState<number | null>(null);

  // Auto-seleccionar la primera fase al cargar. Si la fase desaparece (delete)
  // volvemos a la primera disponible o a null.
  useEffect(() => {
    if (fases.length === 0) {
      setSelectedFaseId(null);
      return;
    }
    if (selectedFaseId === null || !fases.some(f => f.idFase === selectedFaseId)) {
      setSelectedFaseId(fases[0].idFase);
    }
  }, [fases, selectedFaseId]);

  const selectedFase = useMemo(
    () => fases.find(f => f.idFase === selectedFaseId) ?? null,
    [fases, selectedFaseId]
  );

  return (
    <div className="max-w-5xl">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-stone-900 flex items-center gap-2">
          <Buildings size={16} weight="bold" />
          Fases y Estados del flujo CFIA
        </h2>
        <p className="text-xs text-stone-500 mt-0.5">
          Las fases son las columnas del Kanban de Trámites; cada fase agrupa
          sus estados específicos. Arrastrá para reordenar.
        </p>
      </div>

      {isLoading ? (
        <div className="px-4 py-6 text-sm text-stone-500">Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] gap-4 items-start">
          <FasesList
            fases={fases}
            selectedId={selectedFaseId}
            onSelect={setSelectedFaseId}
          />
          <EstadosList fase={selectedFase} />
        </div>
      )}
    </div>
  );
}

// ============================================================
// Lista de fases (master)
// ============================================================
function FasesList({
  fases, selectedId, onSelect,
}: {
  fases: Fase[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const reorderMut = useReorderFases();
  const [showNew, setShowNew] = useState(false);
  const [localItems, setLocalItems] = useState<Fase[]>(fases);
  useEffect(() => { setLocalItems(fases); }, [fases]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = localItems.findIndex(f => f.idFase === active.id);
    const newIdx = localItems.findIndex(f => f.idFase === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(localItems, oldIdx, newIdx);
    setLocalItems(next);
    reorderMut.mutate(next.map(f => f.idFase));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-stone-700">Fases</h3>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-black bg-brand-green-100 hover:bg-brand-green-200 rounded"
        >
          <Plus size={12} weight="bold" /> Nueva fase
        </button>
      </div>

      <div className="bg-white rounded-lg border border-stone-200">
        {localItems.length === 0 ? (
          <div className="px-4 py-6 text-sm text-stone-500 italic">
            Sin fases. Creá una con el botón de arriba.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localItems.map(f => f.idFase)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-stone-100">
                {localItems.map(f => (
                  <FaseRow
                    key={f.idFase}
                    fase={f}
                    isSelected={f.idFase === selectedId}
                    onSelect={() => onSelect(f.idFase)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {showNew && <NewFaseDialog onClose={() => setShowNew(false)} />}
    </div>
  );
}

function FaseRow({
  fase, isSelected, onSelect,
}: {
  fase: Fase;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const updateMut = useUpdateFase();
  const deleteMut = useDeleteFase();
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: fase.idFase });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [nombre, setNombre] = useState(fase.nombre);
  const [color, setColor] = useState(fase.colorHex || '#888780');
  useEffect(() => {
    setNombre(fase.nombre);
    setColor(fase.colorHex || '#888780');
  }, [fase.nombre, fase.colorHex]);

  function commitNombre() {
    const trimmed = nombre.trim();
    if (!trimmed) { setNombre(fase.nombre); return; }
    if (trimmed === fase.nombre) return;
    updateMut.mutate({ id: fase.idFase, dto: { nombre: trimmed } });
  }
  function commitColor() {
    if (color === (fase.colorHex || '')) return;
    updateMut.mutate({ id: fase.idFase, dto: { colorHex: color } });
  }

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const usage = await api.fases.usage(fase.idFase);
      if (usage.lotes > 0 || usage.estados > 0) {
        window.alert(
          `No se puede eliminar "${fase.nombre}": ${usage.lotes} lote(s) y ${usage.estados} estado(s) la usan.`
        );
        return;
      }
    } catch { /* fallback */ }

    if (!window.confirm(`¿Eliminar la fase "${fase.nombre}"?`)) return;
    try {
      await deleteMut.mutateAsync(fase.idFase);
    } catch (err: any) {
      window.alert(err?.message || 'No se pudo eliminar');
    }
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`flex items-center gap-2 px-3 py-2 group cursor-pointer ${
        isSelected ? 'bg-brand-green-100/15' : isDragging ? 'bg-stone-100' : 'hover:bg-stone-50'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
        className="opacity-30 group-hover:opacity-100 text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing transition-opacity p-0.5 flex-shrink-0"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar para reordenar"
      >
        <DotsSixVertical size={16} weight="bold" />
      </button>
      <input
        type="color"
        value={color}
        onChange={e => setColor(e.target.value)}
        onBlur={commitColor}
        onClick={e => e.stopPropagation()}
        title="Color de la fase"
        className="w-7 h-7 rounded cursor-pointer border border-stone-200 flex-shrink-0"
      />
      <input
        value={nombre}
        onChange={e => setNombre(e.target.value)}
        onBlur={commitNombre}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        onClick={e => e.stopPropagation()}
        placeholder="Nombre"
        className="flex-1 bg-transparent border-0 px-2 py-1 text-sm rounded hover:bg-stone-100 focus:bg-white focus:ring-2 focus:ring-brand-green-100 focus:outline-none min-w-0"
      />
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleteMut.isPending}
        className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition-opacity p-1 rounded hover:bg-stone-100 flex-shrink-0 disabled:opacity-30"
        title="Eliminar fase"
        aria-label="Eliminar fase"
      >
        <Trash size={14} weight="bold" />
      </button>
    </li>
  );
}

// ============================================================
// Lista de estados (detail) de la fase seleccionada
// ============================================================
function EstadosList({ fase }: { fase: Fase | null }) {
  const { data: estados = [], isLoading } = useEstadosLote(
    fase ? { idFase: fase.idFase } : {}
  );
  const reorderMut = useReorderEstadosLote();
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<EstadoLote | null>(null);

  // Filtramos a la fase actual: la query con idFase ya lo hace, pero por
  // si el cache no se invalido todavia evitamos mostrar estados de otra fase.
  const filtered = useMemo(
    () => (fase ? estados.filter(e => e.idFase === fase.idFase) : []),
    [estados, fase]
  );

  const [localItems, setLocalItems] = useState<EstadoLote[]>(filtered);
  useEffect(() => { setLocalItems(filtered); }, [filtered]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id || !fase) return;
    const oldIdx = localItems.findIndex(x => x.idEstadoLote === active.id);
    const newIdx = localItems.findIndex(x => x.idEstadoLote === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(localItems, oldIdx, newIdx);
    setLocalItems(next);
    reorderMut.mutate({
      idFase: fase.idFase,
      orderedIds: next.map(x => x.idEstadoLote),
    });
  }

  if (!fase) {
    return (
      <div className="bg-white rounded-lg border border-stone-200 px-4 py-8 text-center text-sm text-stone-500 italic">
        Seleccioná una fase para ver sus estados.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-stone-700">
          Estados de <span className="text-stone-900">{fase.nombre}</span>
        </h3>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-black bg-brand-green-100 hover:bg-brand-green-200 rounded"
        >
          <Plus size={12} weight="bold" /> Nuevo estado
        </button>
      </div>

      <div className="bg-white rounded-lg border border-stone-200">
        {isLoading ? (
          <div className="px-4 py-6 text-sm text-stone-500">Cargando…</div>
        ) : localItems.length === 0 ? (
          <div className="px-4 py-6 text-sm text-stone-500 italic">
            Sin estados en esta fase. Creá uno con el botón de arriba.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localItems.map(x => x.idEstadoLote)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-stone-100">
                {localItems.map(e => (
                  <EstadoRow
                    key={e.idEstadoLote}
                    estado={e}
                    onEdit={() => setEditing(e)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {showNew && (
        <EstadoDialog
          mode="create"
          idFase={fase.idFase}
          onClose={() => setShowNew(false)}
        />
      )}
      {editing && (
        <EstadoDialog
          mode="edit"
          initial={editing}
          idFase={editing.idFase}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function EstadoRow({
  estado, onEdit,
}: {
  estado: EstadoLote;
  onEdit: () => void;
}) {
  const deleteMut = useDeleteEstadoLote();
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: estado.idEstadoLote });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const usage = await api.estadosLote.usage(estado.idEstadoLote);
      if (usage.historicos > 0) {
        window.alert(
          `No se puede eliminar "${estado.estado}": ${usage.historicos} fila(s) del histórico lo referencian.`
        );
        return;
      }
    } catch { /* fallback */ }

    if (!window.confirm(`¿Eliminar el estado "${estado.estado}"?`)) return;
    try {
      await deleteMut.mutateAsync(estado.idEstadoLote);
    } catch (err: any) {
      window.alert(err?.message || 'No se pudo eliminar');
    }
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={onEdit}
      className={`flex items-center gap-2 px-3 py-2 group cursor-pointer ${
        isDragging ? 'bg-stone-100' : 'hover:bg-stone-50'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
        className="opacity-30 group-hover:opacity-100 text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing transition-opacity p-0.5 flex-shrink-0"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar para reordenar"
      >
        <DotsSixVertical size={16} weight="bold" />
      </button>

      <span
        className="w-3 h-3 rounded-full flex-shrink-0"
        style={{ background: estado.colorHex || '#cbd5e1' }}
        title={estado.colorHex || 'Sin color (hereda de la fase)'}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm">
          <span className="font-medium text-stone-800 truncate">{estado.estado}</span>
          {estado.tgCaminoFeliz && (
            <CheckSquare
              size={12}
              weight="bold"
              className="text-brand-green-200 flex-shrink-0"
              aria-label="Camino feliz"
            />
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-stone-500">
          <span className="font-medium">{estado.etapa}</span>
          {estado.descripcion && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{estado.descripcion}</span>
            </>
          )}
        </div>
      </div>

      {estado.diasProyectados != null && (
        <span
          className="text-[10px] text-stone-500 font-mono bg-stone-100 px-1.5 py-0.5 rounded flex-shrink-0"
          title="Dias proyectados"
        >
          {estado.diasProyectados}d
        </span>
      )}
      {estado.tipoProyecto && (
        <span className="text-[10px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap">
          {estado.tipoProyecto}
        </span>
      )}

      <button
        type="button"
        onClick={e => { e.stopPropagation(); onEdit(); }}
        className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-stone-700 transition-opacity p-1 rounded hover:bg-stone-100 flex-shrink-0"
        title="Editar"
        aria-label="Editar"
      >
        <NotePencil size={14} weight="bold" />
      </button>
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleteMut.isPending}
        className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition-opacity p-1 rounded hover:bg-stone-100 flex-shrink-0 disabled:opacity-30"
        title="Eliminar"
        aria-label="Eliminar"
      >
        <Trash size={14} weight="bold" />
      </button>
    </li>
  );
}

// ============================================================
// Dialog de nueva fase (compacto)
// ============================================================
function NewFaseDialog({ onClose }: { onClose: () => void }) {
  const createMut = useCreateFase();
  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState('#888780');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const canSubmit = nombre.trim().length > 0;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createMut.mutateAsync({ nombre: nombre.trim(), colorHex: color });
      onClose();
    } catch (err: any) {
      window.alert(err?.message || 'No se pudo crear la fase');
    }
  }

  return (
    <ModalShell onClose={onClose} title="Nueva fase">
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">Nombre</label>
        <input
          ref={inputRef}
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
          placeholder="Ej. Tasacion CFIA"
          className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">Color</label>
        <input
          type="color"
          value={color}
          onChange={e => setColor(e.target.value)}
          className="w-full h-9 rounded cursor-pointer border border-stone-200"
        />
      </div>
      <ModalActions
        onClose={onClose}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        pending={createMut.isPending}
        label="Crear"
      />
    </ModalShell>
  );
}

// ============================================================
// Dialog de estado (create + edit). Lleva todos los campos: Etapa,
// Estado, Descripcion, TgCaminoFeliz, DiasProyectados, TipoProyecto,
// ColorHex. La fase a la que pertenece se setea desde fuera (en create
// es la fase seleccionada; en edit es la del estado).
// ============================================================
function EstadoDialog({
  mode, initial, idFase, onClose,
}: {
  mode: 'create' | 'edit';
  initial?: EstadoLote;
  idFase: number;
  onClose: () => void;
}) {
  const createMut = useCreateEstadoLote();
  const updateMut = useUpdateEstadoLote();

  const [etapa, setEtapa] = useState(initial?.etapa ?? '');
  const [estado, setEstado] = useState(initial?.estado ?? '');
  const [descripcion, setDescripcion] = useState(initial?.descripcion ?? '');
  const [tgCaminoFeliz, setTgCaminoFeliz] = useState(initial?.tgCaminoFeliz ?? false);
  const [diasProyectados, setDiasProyectados] = useState<string>(
    initial?.diasProyectados != null ? String(initial.diasProyectados) : ''
  );
  const [tipoProyecto, setTipoProyecto] = useState(initial?.tipoProyecto ?? 'Permiso Vivienda');
  const [colorHex, setColorHex] = useState(initial?.colorHex ?? '#888780');

  const firstInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { firstInputRef.current?.focus(); }, []);

  const canSubmit = etapa.trim().length > 0 && estado.trim().length > 0;

  async function handleSubmit() {
    if (!canSubmit) return;
    const dias = diasProyectados.trim() === '' ? null : Number(diasProyectados);
    if (dias != null && (isNaN(dias) || dias < 0)) {
      window.alert('Dias proyectados debe ser un numero positivo o vacio.');
      return;
    }
    try {
      if (mode === 'create') {
        await createMut.mutateAsync({
          idFase,
          etapa: etapa.trim(),
          estado: estado.trim(),
          descripcion: descripcion.trim() || null,
          tgCaminoFeliz,
          diasProyectados: dias,
          tipoProyecto: tipoProyecto.trim() || null,
          colorHex,
        });
      } else if (initial) {
        await updateMut.mutateAsync({
          id: initial.idEstadoLote,
          dto: {
            etapa: etapa.trim(),
            estado: estado.trim(),
            descripcion: descripcion.trim() || null,
            tgCaminoFeliz,
            diasProyectados: dias,
            tipoProyecto: tipoProyecto.trim() || null,
            colorHex,
          },
        });
      }
      onClose();
    } catch (err: any) {
      window.alert(err?.message || 'No se pudo guardar el estado');
    }
  }

  const pending = createMut.isPending || updateMut.isPending;

  return (
    <ModalShell
      onClose={onClose}
      title={mode === 'create' ? 'Nuevo estado' : `Editar estado`}
      wide
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Etapa</label>
          <input
            ref={firstInputRef}
            value={etapa}
            onChange={e => setEtapa(e.target.value)}
            placeholder="Ej. APC CFIA"
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Estado</label>
          <input
            value={estado}
            onChange={e => setEstado(e.target.value)}
            placeholder="Ej. Tasado"
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">Descripcion</label>
        <textarea
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Tooltip que verá el usuario en el kanban"
          rows={2}
          className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100 resize-none"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px] gap-3">
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Tipo de proyecto</label>
          <select
            value={tipoProyecto}
            onChange={e => setTipoProyecto(e.target.value)}
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          >
            <option value="">— Cualquiera —</option>
            {TIPOS_PROYECTO.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Días proyectados</label>
          <input
            type="number"
            min={0}
            value={diasProyectados}
            onChange={e => setDiasProyectados(e.target.value)}
            placeholder="opcional"
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Color</label>
          <input
            type="color"
            value={colorHex}
            onChange={e => setColorHex(e.target.value)}
            className="w-full h-9 rounded cursor-pointer border border-stone-200"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={tgCaminoFeliz}
          onChange={e => setTgCaminoFeliz(e.target.checked)}
          className="sr-only"
        />
        {tgCaminoFeliz
          ? <CheckSquare size={18} weight="bold" className="text-brand-green-200" />
          : <Square size={18} weight="bold" className="text-stone-400" />}
        Camino feliz (flujo sin observaciones)
      </label>

      <ModalActions
        onClose={onClose}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        pending={pending}
        label={mode === 'create' ? 'Crear' : 'Guardar'}
      />
    </ModalShell>
  );
}

// ============================================================
// Shells de modal reutilizables (locales al panel; no exportados).
// ============================================================
function ModalShell({
  onClose, title, wide = false, children,
}: {
  onClose: () => void;
  title: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-lg w-full ${wide ? 'max-w-xl' : 'max-w-md'} p-5 space-y-4`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded" aria-label="Cerrar">
            <X size={18} weight="bold" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({
  onClose, onSubmit, canSubmit, pending, label,
}: {
  onClose: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  pending: boolean;
  label: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        onClick={onClose}
        className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50"
      >
        Cancelar
      </button>
      <button
        onClick={onSubmit}
        disabled={!canSubmit || pending}
        className="px-4 py-1.5 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
      >
        <FloppyDisk size={12} weight="bold" />
        {pending ? 'Guardando…' : label}
      </button>
    </div>
  );
}
